// @ts-nocheck
import type { ItemMetricKey } from '../../types/binding';
import type { ExpressionDefinition, FieldExpressionBinding } from '../../types/canonical';
import {
  collectItemMetricRefsFromAstRoot,
  collectItemMetricRefsFromDataRef,
} from '../metrics/collectItemMetricRefs';
import type { DataRef } from '../../types/binding';
import type { CompiledRuntimeNode, CompiledRuntimeTemplate } from '../compiler';

function expressionRootById(
  expressions: readonly ExpressionDefinition[] | undefined,
  expressionId: string,
): Record<string, unknown> | undefined {
  const def = expressions?.find((e) => e.id === expressionId);
  return def?.root as Record<string, unknown> | undefined;
}

function collectFromStyleBlock(
  style: Record<string, unknown> | undefined,
  expressions: readonly ExpressionDefinition[] | undefined,
  out: Set<ItemMetricKey>,
): void {
  if (!style) {
    return;
  }
  for (const value of Object.values(style)) {
    collectFromValueSource(value, expressions, out);
  }
}

function collectFromValueSource(
  source: unknown,
  expressions: readonly ExpressionDefinition[] | undefined,
  out: Set<ItemMetricKey>,
): void {
  if (!source || typeof source !== 'object') {
    return;
  }
  const vs = source as Record<string, unknown>;
  if (vs.kind === 'binding' && vs.ref != null && typeof vs.ref === 'object') {
    for (const metric of collectItemMetricRefsFromDataRef(vs.ref as DataRef)) {
      out.add(metric);
    }
    return;
  }
  if (vs.kind === 'expression' && typeof vs.expressionId === 'string') {
    const root = expressionRootById(expressions, vs.expressionId);
    for (const metric of collectItemMetricRefsFromAstRoot(root)) {
      out.add(metric);
    }
  }
}

function collectFromValueOrExpression(
  value: unknown,
  expressions: readonly ExpressionDefinition[] | undefined,
  out: Set<ItemMetricKey>,
): void {
  if (
    value &&
    typeof value === 'object' &&
    (value as { kind?: string }).kind === 'expression' &&
    typeof (value as { expressionId?: string }).expressionId === 'string'
  ) {
    const root = expressionRootById(expressions, (value as { expressionId: string }).expressionId);
    for (const metric of collectItemMetricRefsFromAstRoot(root)) {
      out.add(metric);
    }
  }
}

function deepWalkMetricRefs(
  value: unknown,
  expressions: readonly ExpressionDefinition[] | undefined,
  out: Set<ItemMetricKey>,
  depth = 0,
): void {
  if (value == null || depth > 16) {
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      deepWalkMetricRefs(item, expressions, out, depth + 1);
    }
    return;
  }
  if (typeof value !== 'object') {
    return;
  }
  const obj = value as Record<string, unknown>;
  const kind = obj.kind;
  if (kind === 'binding' || kind === 'expression') {
    collectFromValueSource(obj, expressions, out);
    return;
  }
  if (kind === 'static') {
    return;
  }
  if (kind === 'literal' || kind === 'expression') {
    collectFromValueOrExpression(obj, expressions, out);
    return;
  }
  for (const child of Object.values(obj)) {
    deepWalkMetricRefs(child, expressions, out, depth + 1);
  }
}

export function collectLayoutMetricRefs(
  nodes: readonly CompiledRuntimeNode[],
  expressions: readonly ExpressionDefinition[] | undefined,
): ItemMetricKey[] {
  const out = new Set<ItemMetricKey>();
  for (const node of nodes) {
    if (node.visibilitySource !== undefined) {
      collectFromValueSource(node.visibilitySource, expressions, out);
    } else if (node.visibilityExpressionId) {
      const root = expressionRootById(expressions, node.visibilityExpressionId);
      for (const metric of collectItemMetricRefsFromAstRoot(root)) {
        out.add(metric);
      }
    }
    if (node.enabledSource !== undefined) {
      collectFromValueSource(node.enabledSource, expressions, out);
    } else if (node.enabledExpressionId) {
      const root = expressionRootById(expressions, node.enabledExpressionId);
      for (const metric of collectItemMetricRefsFromAstRoot(root)) {
        out.add(metric);
      }
    }
    collectFromValueSource(node.layout.rotation, expressions, out);
    collectFromStyleBlock(node.style as Record<string, unknown> | undefined, expressions, out);
    deepWalkMetricRefs(node.props, expressions, out);
  }
  return [...out];
}

export function collectAllPipelineMetricRefs(options: {
  fieldBindings?: readonly FieldExpressionBinding[];
  layoutNodes?: readonly CompiledRuntimeNode[];
  expressions?: readonly ExpressionDefinition[];
  computedExpressionRoots?: Array<Record<string, unknown> | undefined>;
}): ItemMetricKey[] {
  const out = new Set<ItemMetricKey>();

  for (const binding of options.fieldBindings ?? []) {
    if (binding.value.kind === 'expression') {
      const root = expressionRootById(options.expressions, binding.value.expressionId);
      for (const metric of collectItemMetricRefsFromAstRoot(root)) {
        out.add(metric);
      }
    }
  }

  for (const metric of collectLayoutMetricRefs(options.layoutNodes ?? [], options.expressions)) {
    out.add(metric);
  }

  for (const root of options.computedExpressionRoots ?? []) {
    for (const metric of collectItemMetricRefsFromAstRoot(root)) {
      out.add(metric);
    }
  }

  return [...out];
}

export function listCompiledNodesForElement(
  compiled: CompiledRuntimeTemplate,
  elementId: string,
  layoutId?: string,
): CompiledRuntimeNode[] {
  const childSet = new Set(Object.values(compiled.indexes.childNodeIdsByParentId).flat());
  return Object.values(compiled.nodesById).filter((node) => {
    if (node.elementId !== elementId || childSet.has(node.id)) {
      return false;
    }
    if (layoutId !== undefined) {
      return node.layoutId === layoutId;
    }
    return true;
  });
}
